/**
 * Email validation utilities shared across frontend and backend
 */

// RFC 5321 specifies max email length of 320 characters
const MAX_EMAIL_LENGTH = 320

/**
 * Validates if a string is a properly formatted email address.
 * Uses a comprehensive regex pattern that checks for:
 * - Valid characters in the local part (before @)
 * - Proper domain format with at least one dot
 * - Top-level domain with at least 2 characters
 */
export function isValidEmail(email: string): boolean {
  // Trim whitespace before validation
  const trimmedEmail = email.trim()

  if (trimmedEmail.length > MAX_EMAIL_LENGTH) {
    return false
  }

  // Check for multiple @ symbols
  const atCount = (trimmedEmail.match(/@/g) || []).length
  if (atCount !== 1) {
    return false
  }

  // Split email to validate domain part separately
  const [localPart, domain] = trimmedEmail.split('@')
  if (!localPart || !domain) {
    return false
  }

  // Additional validation checks
  if (trimmedEmail.includes('..')) {
    return false
  }

  // Don't allow domain to start or end with dot
  if (domain.startsWith('.') || domain.endsWith('.')) {
    return false
  }

  // Local part validation
  const localPartRegex = /^[A-Z0-9._%+-]+$/i
  if (!localPartRegex.test(localPart)) {
    return false
  }

  // Domain validation: allow either:
  // 1. Simple hostname without dots (like "example" or "localhost")
  // 2. FQDN with proper TLD of at least 2 characters
  const domainWithoutTld = /^[A-Z0-9]([A-Z0-9-]*[A-Z0-9])?$/i
  const domainWithTld = /^[A-Z0-9][A-Z0-9.-]*[A-Z0-9]\.[A-Z]{2,}$/i

  // If domain has dots, it must be a valid FQDN with proper TLD
  if (domain.includes('.')) {
    return domainWithTld.test(domain)
  } else {
    // If no dots, it's a simple hostname - allow it
    return domainWithoutTld.test(domain)
  }
}

/**
 * Normalizes an email address by:
 * - Trimming whitespace
 * - Converting to lowercase
 *
 * @param email - The email address to normalize
 * @returns The normalized email address
 */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

/**
 * Subdomain validation utilities
 */

export type SubdomainValidationResult =
  | { isValid: true; normalized: string }
  | { isValid: false; error: string }

/**
 * Validates if a string is a valid subdomain (not a full URL or domain).
 * Rules:
 * - Cannot contain periods (would indicate a full domain)
 * - Cannot contain protocol prefixes (://)
 * - Can only contain letters, numbers, and hyphens
 *
 * @param subdomain - The subdomain string to validate
 * @returns Validation result with normalized value if valid, or error message if invalid
 */
export function validateSubdomain(subdomain: string): SubdomainValidationResult {
  const trimmed = subdomain.trim()

  if (trimmed.includes('.')) {
    return { isValid: false, error: 'Enter only the subdomain, not a full URL or domain' }
  }

  if (trimmed.includes('://')) {
    return { isValid: false, error: 'Enter only the subdomain, not a full URL' }
  }

  if (!/^[a-zA-Z0-9-]*$/.test(trimmed)) {
    return { isValid: false, error: 'Subdomain can only contain letters, numbers, and hyphens' }
  }

  return { isValid: true, normalized: trimmed }
}

/**
 * Returns true if the subdomain is valid
 */
export function isValidSubdomain(subdomain: string): boolean {
  return validateSubdomain(subdomain).isValid
}
